import { BaseApiService } from '@/shared/api/services/base.service';

export interface LoadingFactItem {
  id: string;
  type: 'fact' | 'tip' | 'trivia';
  text: string;
}

export interface LoadingFactsAttributes {
  facts: LoadingFactItem[];
  is_contextual: boolean;
  seen_ids: string[];
  // Static, localized design stages narrated above the progress bar during
  // render generation (server single-source: lang/generation.php). Always
  // present, independent of the LLM facts pool.
  stages: string[];
}

interface LoadingFactsResponse {
  data: {
    type: string;
    id: string;
    attributes: LoadingFactsAttributes;
  };
}

// Loading-facts is non-critical UX decor. Treat 4xx (deleted/cross-account
// project, etc.) as "no data yet" rather than Sentry-worthy errors, matching
// Apple's Log.network.debug behaviour and avoiding the Phase-1 review noise
// from stale projectIds in TanStack Query trees. WebSocket push remains the
// authoritative delivery path.
const SILENT = { error: { silent: true } } as const;

class LoadingFactsService extends BaseApiService {
  async getFacts(projectId: string): Promise<LoadingFactsAttributes> {
    const response = await this.httpGet<LoadingFactsResponse>(
      `/v1/projects/${projectId}/loading-facts`,
      undefined,
      SILENT
    );
    return response.data.attributes;
  }

  async markSeen(factIds: string[]): Promise<void> {
    await this.post(
      '/v1/projects/loading-facts/seen',
      { fact_ids: factIds },
      undefined,
      SILENT
    );
  }

  async regenerate(projectId: string): Promise<void> {
    await this.post(
      `/v1/projects/${projectId}/loading-facts/regenerate`,
      {},
      undefined,
      SILENT
    );
  }
}

export const loadingFactsService = new LoadingFactsService();
